import React, { FC, useEffect, useState } from 'react'
import { Link } from 'gatsby'
import clsx from 'clsx'

import Layout from '@/components/Layout'
import Social from '@/components/Social'

/**
 * @interface MenuListProps
 * @description Defines the properties for the MenuList component.
 */
interface MenuListProps {
	/** Current state of the menu */
	isMenuOpen: boolean
	/** Callback function triggered on click */
	toggleOpenMenu: () => void
}

/**
 * @interface MenuItemsData
 * @description Interface for menu items data.
 */
interface MenuItemsData {
	id: number;
	title: string;
	url: string;
}

const MENU_LIST: MenuItemsData[] = [
	{ id: 1, title: 'สำหรับองค์กร', url: '/' },
	{ id: 2, title: 'สำหรับบุคคลทั่วไป', url: '/b2c' },
	{ id: 3, title: 'บทความ', url: '/blog' },
	{ id: 4, title: 'เกี่ยวกับเรา', url: '/about' },
	{ id: 5, title: 'ติดต่อเรา', url: '/contact' }
]

/**
 * @component List
 * @description Full-screen navigation overlay. Does not mount until the user opens
 * the menu for the first time — keeps the 100vh overlay out of initial layout,
 * eliminating its contribution to layout cost and TBT.
 * Uses CSS transitions for open/close animation (no GSAP dependency).
 */
const List: FC<MenuListProps> = ({ isMenuOpen, toggleOpenMenu }) => {
	const [hasMounted, setHasMounted] = useState(false)

	useEffect(() => {
		// eslint-disable-next-line react-hooks/set-state-in-effect
		if (isMenuOpen && !hasMounted) setHasMounted(true)
	}, [isMenuOpen, hasMounted])

	if (!hasMounted) return null

	return (
		<div
			className={clsx(
				'fixed top-16 left-0 w-full h-[calc(100vh-64px)] z-40 bg-green-500 py-6 pb-16',
				'sm:h-[calc(100vh-120px)] sm:py-0 sm:pb-22 sm:top-30',
				'transition-all duration-300',
				isMenuOpen
					? 'opacity-100 visible translate-y-0 pointer-events-auto'
					: 'opacity-0 invisible -translate-y-2.5 pointer-events-none'
			)}
		>
			<Layout
				classNameContainer="h-full"
				classNameInnerContainer={clsx(
					'flex flex-col justify-between gap-2 relative h-full',
					'sm:gap-8.75'
				)}
			>
				<div className={clsx(
					'flex flex-col gap-2 h-full justify-start',
					'sm:gap-4 sm:justify-center'
				)}>
					{ MENU_LIST.map((item) => (
						<Link
							key={ item.id }
							to={ item.url }
							className="primary/h4 sm:primary/h2 text-white hover:text-green-200 w-fit"
							onClick={ toggleOpenMenu }
						>
							{ item.title }
						</Link>
					)) }
				</div>
				<Social />
			</Layout>
		</div>
	)
}

export default List
